import { DataTable } from '@/components/dataTable';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useDynamicLocale } from '@/hoslog-intl';
import { exportCsvSingleFilePapa } from '@/lib/exportCsv';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import type { CustomerReferralDetail } from '@/services/dashboard/referralReports/referralReports.types';
import * as React from 'react';
import { getAffiliateCustomerColumns } from './Column';

export function AffiliateCustomerReferralTable({
  data: apiData,
  pagination,
  isLoading = false,
}: {
  data: CustomerReferralDetail[];
  pagination: Pagination;
  isLoading?: boolean;
}) {
  const { t } = useDynamicLocale();
  const columns = React.useMemo(() => getAffiliateCustomerColumns(t), [t]);
  const data = apiData;
  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );
  const { table } = useDataTable({
    data,
    columns,
    pageCount,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
    meta: {
      defaultDatePreset: 'today',
      title: t('dashboard.referralReports.tableTittle.customerRecords'),
      totalPage: pagination.totalItems,
      pageSize: pagination.pageSize,
      getSerialNumber: (rowIndex: number) =>
        Math.max((pagination.currentPage ?? 1) - 1, 0) * (pagination.pageSize ?? 10) + rowIndex + 1,
      actionKey: 'exportCsv',
      action: () =>
        exportCsvSingleFilePapa(
          table.getFilteredRowModel().rows.map(({ original, index }) => ({
            [t('common.page.tableColumn.sn')]: getSerialNumber(table, index),
            [t('common.page.tableColumn.customerId')]: original.customerId,
            [t('common.page.tableColumn.rewardPoints')]: original.rewardPoints,
            [t('common.page.tableColumn.reservedBy')]: original.reservedBy,
            [t('common.page.tableColumn.storeName')]: original.storeName,
            [t('common.page.tableColumn.storeNameJp')]: original.storeNameJp,
            [t('common.page.tableColumn.visitDateTime')]: original.visitDateTime,
          })),
          { fileName: 'affiliate-customer-referral-report', format: 'YYYY/MM/DD HH:mm:ss' },
        ),
      actionDisabled: data.length === 0,
      loading: isLoading,
    },
    getRowId: (row, index) =>
      [
        row.customerId,
        row.visitDateTime,
        row.storeName,
        Math.max((pagination.currentPage ?? 1) - 1, 0) * (pagination.pageSize ?? 10) + index,
      ].join('-'),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <DataTable className="rounded-t-none" table={table}>
      <DataTableToolbar hideViewOptions buttonText="common.page.table.exportCSV" />
    </DataTable>
  );
}
